import { useState, useEffect } from "react";
import { supabase, BRAND, MUTED, LINE, SURFACE, TEXT, text, btn, linkBtn, segWrap, segBtn, errorBanner, SHADOW_POP, WHITE, INK, FAINT } from "./constants";
import { Check, CircleAlert } from "lucide-react";
import { useIsMobile } from "./lib/responsive";
import Logo from "./Logo";
import { PageLoader } from "./LogoLoader";

const FULL_POLICY = `PRIVACY AND DATA PROTECTION POLICY

Data Controller
RUTMENT TECH S.L.U. (in constitution), registered address at Calle Serrano Anguita 7, 5th floor, 28004 Madrid, Spain. Contact email: borja@rutment.com.

Purpose of Processing
Personal data provided through this form will be processed exclusively for the following purposes:
• To evaluate your candidacy in the selection process for which you have been contacted.
• To manage communication between the candidate and the Rutment team and/or the client company throughout the process.
• To create a professional profile based on your responses, which will be shared confidentially with the client company.

Legal Basis
The processing of your data is based on the explicit consent you provide by accepting this policy, in accordance with Article 6.1(a) of the General Data Protection Regulation (GDPR).

Data Processed
We will process the data you provide directly in this form (name, city, salary, notice period and questionnaire responses), as well as professional information publicly available on LinkedIn, including: previous and current positions, education, profile information (including profile photo), publications and professional contact information. This information may be consulted, stored and used as part of the evaluation of your candidacy.

Recipients
Your data may be shared with the client company for which the selection process is being conducted, under a confidentiality agreement. We will not share your data with third parties for other purposes without your explicit consent.

Data Retention
We will retain your data for as long as necessary to manage the active selection process and, thereafter, in our candidate database for potential future opportunities that may match your profile. You may withdraw your consent and request the complete deletion of your data at any time by contacting us. We will process deletion requests within 30 business days.

Your Rights
Under the GDPR, you have the right to:
• Access your personal data.
• Rectify inaccurate or incomplete data.
• Request the deletion of your data (right to be forgotten).
• Object to processing or request its restriction.
• Request the portability of your data.

To exercise any of these rights, including the complete deletion of your data from our systems, please send an email to borja@rutment.com with the subject line "GDPR Rights Request" and a copy of your identity document. We will respond within a maximum of 30 business days. If you believe that the processing does not comply with applicable regulations, you have the right to file a complaint with the Spanish Data Protection Agency (www.aepd.es).

Security
Rutment applies appropriate technical and organisational measures to ensure the security of your data and prevent unauthorised access, loss or destruction.

Policy Updates
This policy may be updated. In the event of significant changes, we will notify you through the available communication channels.`;

const FULL_POLICY_ES = `POLÍTICA DE PRIVACIDAD Y PROTECCIÓN DE DATOS

Responsable del tratamiento
RUTMENT TECH S.L.U. (en constitución), con domicilio en Calle Serrano Anguita 7, 5.ª planta, 28004 Madrid, España. Email de contacto: borja@rutment.com.

Finalidad del tratamiento
Los datos personales que facilites en este formulario se tratarán exclusivamente para las siguientes finalidades:
• Evaluar tu candidatura en el proceso de selección para el que se te ha contactado.
• Gestionar la comunicación entre el candidato y el equipo de Rutment y/o la empresa cliente durante el proceso.
• Elaborar un perfil profesional a partir de tus respuestas, que se compartirá de forma confidencial con la empresa cliente.

Base legal
El tratamiento de tus datos se basa en el consentimiento explícito que otorgas al aceptar esta política, conforme al artículo 6.1.a) del Reglamento General de Protección de Datos (RGPD).

Datos tratados
Trataremos los datos que facilites directamente en este formulario (nombre, ciudad, salario, preaviso y respuestas al cuestionario), así como la información profesional disponible públicamente en LinkedIn, incluidos: puestos anteriores y actuales, formación, información del perfil (incluida la foto de perfil), publicaciones y datos de contacto profesional. Esta información podrá consultarse, almacenarse y utilizarse como parte de la evaluación de tu candidatura.

Destinatarios
Tus datos podrán compartirse con la empresa cliente para la que se realiza el proceso de selección, bajo un acuerdo de confidencialidad. No cederemos tus datos a terceros con otros fines sin tu consentimiento explícito.

Conservación de los datos
Conservaremos tus datos durante el tiempo necesario para gestionar el proceso de selección activo y, después, en nuestra base de datos de candidatos para posibles oportunidades futuras que encajen con tu perfil. Puedes retirar tu consentimiento y solicitar la eliminación completa de tus datos en cualquier momento contactándonos. Atenderemos las solicitudes de eliminación en un plazo de 30 días hábiles.

Tus derechos
Conforme al RGPD, tienes derecho a:
• Acceder a tus datos personales.
• Rectificar los datos inexactos o incompletos.
• Solicitar la supresión de tus datos (derecho al olvido).
• Oponerte al tratamiento o solicitar su limitación.
• Solicitar la portabilidad de tus datos.

Para ejercer cualquiera de estos derechos, incluida la eliminación completa de tus datos de nuestros sistemas, envía un email a borja@rutment.com con el asunto "Solicitud de derechos RGPD" y una copia de tu documento de identidad. Responderemos en un plazo máximo de 30 días hábiles. Si consideras que el tratamiento no cumple la normativa aplicable, tienes derecho a presentar una reclamación ante la Agencia Española de Protección de Datos (www.aepd.es).

Seguridad
Rutment aplica las medidas técnicas y organizativas adecuadas para garantizar la seguridad de tus datos y evitar su acceso no autorizado, pérdida o destrucción.

Actualizaciones de la política
Esta política puede actualizarse. Si hay cambios significativos, te lo comunicaremos por los canales de comunicación disponibles.`;

// Mensajes de error por código: se traducen al pintar, así el cambio de idioma
// también los cambia.
const ERROR_TEXT = {
  invalid: { es: "Este enlace no es válido.", en: "This link is not valid." },
  expired: { es: "Este enlace ha caducado. Contacta con tu recruiter.", en: "This link has expired. Please contact your recruiter." },
  "no-form": { es: "Esta posición todavía no tiene formulario de cualificación.", en: "This position does not have a qualification form yet." },
  generic: { es: "Se ha producido un error. Inténtalo de nuevo.", en: "An error occurred. Please try again." },
};

const QualFormView = ({ token }) => {
  // Avisos en la página, junto al botón (24/10): antes eran alert() del navegador
  const [formErr, setFormErr] = useState("");
  const [lang, setLang] = useState("en");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [showFullPolicy, setShowFullPolicy] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => { loadForm(); }, [token]);

  const loadForm = async () => {
    setLoading(true);
    try {
      // Una función comprueba el token y devuelve solo las preguntas. Antes se
      // leía qual_tokens y positions directamente, con reglas abiertas (06/10).
      const { data: pos, error: rpcErr } = await supabase.rpc("get_qual_form", { p_token: token });

      // Idioma inicial: el de la posición si la función lo devuelve; si no, inglés
      const posLang = pos?.lang || pos?.language;
      if (posLang === "es" || posLang === "en") setLang(posLang);

      if (rpcErr || !pos || pos.error === "invalid") { setError("invalid"); setLoading(false); return; }
      if (pos.completed) { setSubmitted(true); setLoading(false); return; }
      if (pos.error === "expired") { setError("expired"); setLoading(false); return; }


      if (pos?.qual_questions) {
        setQuestions(pos.qual_questions);
        const init = {};
        pos.qual_questions.forEach(s => s.items.forEach(item => { init[item.n] = ""; }));
        setAnswers(init);
      } else {
        setError("no-form");
      }
    } catch (e) {
      console.error(e);
      setError("generic");
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    setFormErr("");
    if (!policyAccepted) {
      setFormErr(lang === "es" ? "Acepta la política de privacidad antes de enviar." : "Please accept the privacy policy before submitting.");
      return;
    }
    // All fields mandatory
    const allItems = questions.flatMap(s => s.items);
    for (const item of allItems) {
      if (!answers[item.n]?.trim()) {
        setFormErr(lang === "es"
          ? `Responde a todas las preguntas antes de enviar. Falta: ${item.n} • ${item.q}`
          : `Please answer all questions before submitting. Missing: ${item.n} • ${item.q}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const qualSections = questions.map(section => ({
        section: section.section,
        items: section.items.map(item => ({ n: item.n, q: item.q, a: answers[item.n] || "" })),
      }));
      // La función guarda las respuestas y SOLO entonces gasta el enlace. Antes
      // el guardado fallaba en silencio y el enlace se marcaba completado
      // igualmente: las respuestas se perdían y no se podía volver a intentar.
      const { data: res, error: rpcErr } = await supabase.rpc("submit_qual", { p_token: token, p_sections: qualSections });
      if (rpcErr || !res?.ok) {
        console.error(rpcErr || res);
        setFormErr(lang === "es"
          ? "No hemos podido guardar tus respuestas. Inténtalo de nuevo o escribe a borja@rutment.com."
          : "We couldn't save your answers. Please try again, or write to borja@rutment.com.");
        setSubmitting(false);
        return;
      }
      setSubmitted(true);
    } catch (e) {
      console.error(e);
      setFormErr(lang === "es" ? "Se ha producido un error al enviar. Inténtalo de nuevo." : "An error occurred while submitting. Please try again.");
    }
    setSubmitting(false);
  };

  // Móvil: 16px para que iOS no haga zoom al enfocar, y 44 de alto mínimo
  const inputStyle = isMobile
    ? { width: "100%", boxSizing: "border-box", padding: "11px 14px", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: 16, minHeight: 44, outline: "none", fontFamily: "inherit", background: WHITE }
    : { width: "100%", boxSizing: "border-box", padding: "10px 14px", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: 13, outline: "none", fontFamily: "inherit" };
  const pagePad = isMobile ? 16 : 24;

  if (loading) return <PageLoader minHeight="100vh" background={SURFACE} label={lang === "es" ? "Cargando tu formulario..." : "Loading your form..."} />;

  if (error) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: SURFACE, fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif", padding: pagePad }}>
      <div style={{ background: WHITE, borderRadius: 16, padding: isMobile ? "32px 20px" : "40px 48px", maxWidth: 480, width: "100%", textAlign: "center", boxShadow: SHADOW_POP }}>
        <div style={{ width: 48, height: 48, borderRadius: "50%", background: SURFACE, margin: "0 auto 16px", display: "flex", alignItems: "center", justifyContent: "center" }}><CircleAlert size={22} strokeWidth={1.75} color={MUTED} /></div>
        <div style={{ ...text.cardTitle, marginBottom: 8 }}>{lang === "es" ? "Enlace no disponible" : "Link not available"}</div>
        <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.6 }}>{(ERROR_TEXT[error] || ERROR_TEXT.generic)[lang]}</div>
      </div>
    </div>
  );

  if (submitted) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: SURFACE, fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif", padding: pagePad }}>
      <div style={{ background: WHITE, borderRadius: 16, padding: isMobile ? "32px 20px" : "40px 48px", maxWidth: 480, width: "100%", textAlign: "center", boxShadow: SHADOW_POP }}>
        <div style={{ width: 48, height: 48, background: SURFACE, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}><Check size={22} strokeWidth={1.75} color={TEXT} /></div>
        <div style={{ fontWeight: 800, fontSize: 22, color: INK, marginBottom: 8 }}>{lang === "es" ? "¡Gracias!" : "Thank you!"}</div>
        <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.6 }}>{lang === "es" ? "Hemos recibido tus respuestas. Nuestro equipo se pondrá en contacto contigo pronto." : "Your responses have been submitted successfully. Our team will be in touch soon."}</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: SURFACE, fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif" }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>

      {/* Header — matches Sidebar exactly */}
      <div style={{ background: WHITE, borderBottom: `1.5px solid ${LINE}`, padding: isMobile ? "8px 16px" : "16px 32px", display: "flex", alignItems: "center", gap: 8 }}>
        <Logo size={20} />
        {!isMobile && <div style={{ fontSize: 11, color: MUTED }}>{lang === "es" ? "Formulario de candidato" : "Candidate form"}</div>}
        {/* Selector de idioma: mismo que ReferenceFormView */}
        <div style={{ marginLeft: "auto", ...segWrap }}>
          {["es", "en"].map(l => (
            <button key={l} onClick={() => setLang(l)}
              style={{ ...segBtn(lang === l), padding: "5px 10px", fontSize: 11, textTransform: "uppercase", ...(isMobile ? { minWidth: 44, minHeight: 36, fontSize: 12 } : {}) }}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: isMobile ? "24px auto" : "40px auto", padding: isMobile ? "0 16px calc(40px + env(safe-area-inset-bottom))" : "0 24px 80px" }}>

        {/* Privacy policy banner */}
        <div style={{ background: WHITE, border: "1.5px solid " + LINE, borderRadius: 14, padding: isMobile ? "18px 16px" : "20px 24px", marginBottom: isMobile ? 24 : 32 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: INK, marginBottom: 8 }}>{lang === "es" ? "Política de privacidad" : "Privacy policy"}</div>
          <p style={{ fontSize: 13, color: TEXT, lineHeight: 1.7, margin: "0 0 12px" }}>
            {lang === "es"
              ? "Los datos que facilites en este formulario se tratarán exclusivamente para evaluar tu candidatura en el proceso de selección. Conforme al Reglamento General de Protección de Datos (RGPD), garantizamos la confidencialidad y la seguridad de la información, que no se usará con otros fines sin tu consentimiento."
              : "Data provided in this form will be processed exclusively to evaluate your candidacy in the selection process. In accordance with the General Data Protection Regulation (GDPR), we guarantee the confidentiality and security of the information, which will not be used for other purposes without your consent."}
          </p>
          <button onClick={() => setShowFullPolicy(v => !v)} style={{ ...linkBtn, marginBottom: 14, ...(isMobile ? { fontSize: 13, minHeight: 40, marginBottom: 4 } : {}) }}>
            {showFullPolicy
              ? (lang === "es" ? "Ocultar política completa ▲" : "Hide full policy ▲")
              : (lang === "es" ? "Leer política completa ▼" : "Read full policy ▼")}
          </button>
          {showFullPolicy && (
            <div style={{ background: SURFACE, borderRadius: 10, padding: "16px 18px", marginBottom: 14, maxHeight: 280, overflowY: "auto" }}>
              <pre style={{ fontSize: 12, color: TEXT, lineHeight: 1.8, margin: 0, whiteSpace: "pre-wrap", fontFamily: "inherit" }}>{lang === "es" ? FULL_POLICY_ES : FULL_POLICY}</pre>
            </div>
          )}
          <label style={{ display: "flex", alignItems: "flex-start", gap: isMobile ? 12 : 10, cursor: "pointer", ...(isMobile ? { padding: "8px 0" } : {}) }}>
            <input
              type="checkbox"
              checked={policyAccepted}
              onChange={e => setPolicyAccepted(e.target.checked)}
              style={{ marginTop: 2, width: isMobile ? 22 : 16, height: isMobile ? 22 : 16, accentColor: BRAND, cursor: "pointer", flexShrink: 0 }}
            />
            <span style={{ fontSize: isMobile ? 15 : 13, color: INK, lineHeight: 1.5 }}>
              {lang === "es" ? "He leído y acepto la política de privacidad y protección de datos." : "I have read and accept the privacy and data protection policy."} <span style={{ color: BRAND }}>*</span>
            </span>
          </label>
        </div>

        {/* Title */}
        <div style={{ marginBottom: isMobile ? 24 : 32 }}>
          <h1 style={{ fontSize: isMobile ? 22 : 26, fontWeight: 800, color: INK, margin: "0 0 8px", letterSpacing: -0.5 }}>{lang === "es" ? "Cuestionario del proceso" : "Process questionnaire"}</h1>
          <p style={{ fontSize: 13, color: MUTED, margin: 0, lineHeight: 1.6 }}>
            {lang === "es"
              ? "Responde a todas las preguntas con el mayor detalle posible. Nuestro equipo revisará tus respuestas."
              : "Please answer all questions as thoroughly as possible. Your responses will be reviewed by our team."}
          </p>
        </div>

        {/* Questions */}
        {questions.map((section, si) => (
          <div key={si} style={{ marginBottom: isMobile ? 28 : 36 }}>
            <div style={{ ...text.label, marginBottom: 16, paddingBottom: 10, borderBottom: `1.5px solid ${LINE}` }}>
              {section.section}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {section.items.map((item) => (
                <div key={item.n}>
                  <label style={{ display: "block", fontSize: isMobile ? 15 : 13, fontWeight: 600, color: INK, lineHeight: 1.5, marginBottom: 8 }}>
                    <span style={{ color: FAINT, fontWeight: 700, marginRight: 8, fontSize: 11 }}>{item.n}</span>
                    {item.q}
                    <span style={{ color: BRAND, marginLeft: 4 }}>*</span>
                  </label>
                  {item.n.startsWith("1.") ? (
                    <input
                      type="text"
                      value={answers[item.n] || ""}
                      onChange={e => setAnswers(prev => ({ ...prev, [item.n]: e.target.value }))}
                      style={inputStyle}
                      onFocus={e => e.target.style.borderColor = BRAND}
                      onBlur={e => e.target.style.borderColor = LINE}
                      placeholder={lang === "es" ? "Tu respuesta" : "Your answer"}
                    />
                  ) : (
                    <textarea
                      value={answers[item.n] || ""}
                      onChange={e => setAnswers(prev => ({ ...prev, [item.n]: e.target.value }))}
                      rows={item.n.startsWith("2.") || item.n.startsWith("3.") ? 4 : 3}
                      style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }}
                      onFocus={e => e.target.style.borderColor = BRAND}
                      onBlur={e => e.target.style.borderColor = LINE}
                      placeholder={lang === "es" ? "Tu respuesta" : "Your answer"}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Submit */}
        <div style={{ marginTop: isMobile ? 28 : 40, paddingTop: isMobile ? 20 : 28, borderTop: `1.5px solid ${LINE}` }}>
          {!policyAccepted && (
            <p style={{ background: SURFACE, border: "1.5px solid " + LINE, borderRadius: 10, padding: "10px 14px", fontSize: 13, color: TEXT, lineHeight: 1.6, marginBottom: 16 }}>
              {lang === "es" ? "Acepta la política de privacidad de arriba antes de enviar." : "Please accept the privacy policy above before submitting."}
            </p>
          )}
          {formErr && <div role="alert" style={{ ...errorBanner, marginBottom: 12 }}>{formErr}</div>}
          <button
            onClick={handleSubmit}
            disabled={submitting || !policyAccepted}
            style={{ ...(submitting || !policyAccepted ? btn.disabled : btn.primary), padding: "14px 36px", fontSize: 15, width: "100%", transition: "background 0.2s" }}
          >
            {submitting ? (lang === "es" ? "Enviando..." : "Submitting...") : (lang === "es" ? "Enviar cuestionario →" : "Submit questionnaire →")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QualFormView;
